import { READING_TYPES, CRACK_COOKIE_TOKEN_TYPES } from "helpers/appConstants";
import { Stack } from "@mui/material";
import {
  RedAlertIcon,
  ThresholdInputLabel,
  YellowAlertIcon
} from "../Thresholds/Thresholds.styles";
import ThresholdLine from "./ThresholdLine";
import { associateReadingType } from "helpers/variables";
import {
  ThresholdInterface
} from "context/initialState/Threshold";
import API from "api";
import Session from "helpers/session";
import { BodyProps, CellProps } from "components/shared/SimpleGrid/SimpleGrid";
import {
  uStatus,
  uPulse,
  uDiastolic,
  uSystolic,
  uHigh,
  uLow
} from "helpers/translationStringConstants";
import { useTranslation } from "react-i18next";
const { coordinator } = CRACK_COOKIE_TOKEN_TYPES;

const {
  bloodGlucose,
  bloodPressure,
  temperature,
  weight: WEIGHT,
  heartRate,
  oxygenSaturation,
  spirometry,
  activity,
  sleep: SLEEP
} = READING_TYPES;

export const generateInputs = (
  type: string | undefined,
  alertLevel: "moderate" | "high",
  disabled: boolean,
  onChangeCallback?: any,
  visibility?: Record<string, boolean>
) => {
  switch (type) {
    case bloodPressure.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            <ThresholdLine
              type="Systolic"
              alertLevel={alertLevel}
              units="mmHg"
              isGlobal={false}
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
            <ThresholdLine
              type="Diastolic"
              alertLevel={alertLevel}
              units="mmHg"
              isGlobal={false}
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
          </Stack>
        </div>
      );
    case bloodGlucose.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Glucose"
            alertLevel={alertLevel}
            units="mg/dL"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case spirometry.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            {visibility?.fvc !== false && (
              <ThresholdLine
                type="FVC"
                alertLevel={alertLevel}
                units="L"
                isGlobal={false}
                isDisabled={disabled}
                onChangeFunc={onChangeCallback}
              />
            )}
            {visibility?.fev1 !== false && (
              <ThresholdLine
                type="FEV1"
                alertLevel={alertLevel}
                units="L"
                isGlobal={false}
                isDisabled={disabled}
                onChangeFunc={onChangeCallback}
              />
            )}
            {visibility?.fev1fvc !== false && (
              <ThresholdLine
                type="FEV1_FVC"
                alertLevel={alertLevel}
                units="L"
                isGlobal={false}
                isDisabled={disabled}
                onChangeFunc={onChangeCallback}
              />
            )}
            {visibility?.pef !== false && (
              <ThresholdLine
                type="PEF"
                alertLevel={alertLevel}
                units="L"
                isGlobal={false}
                isDisabled={disabled}
                onChangeFunc={onChangeCallback}
              />
            )}
          </Stack>
        </div>
      );
    case oxygenSaturation.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <Stack spacing={2}>
            <ThresholdLine
              type="Oxygen Saturation"
              alertLevel={alertLevel}
              units="%"
              isGlobal={false}
              isDisabled={disabled}
              onChangeFunc={onChangeCallback}
            />
          </Stack>
        </div>
      );
    case temperature.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Temperature"
            alertLevel={alertLevel}
            units="F"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case WEIGHT.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Weight"
            alertLevel={alertLevel}
            units="LBS"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case activity.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Activity"
            alertLevel={alertLevel}
            units="steps"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case SLEEP.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Sleep"
            alertLevel={alertLevel}
            units="hrs"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
    case heartRate.abbreviation:
      return (
        <div>
          <Stack direction="row">
            <ThresholdInputLabel>low</ThresholdInputLabel>
            <ThresholdInputLabel>high</ThresholdInputLabel>
          </Stack>
          <ThresholdLine
            type="Pulse"
            alertLevel={alertLevel}
            units="bpm"
            isGlobal={false}
            isDisabled={disabled}
            onChangeFunc={onChangeCallback}
          />
        </div>
      );
  }
};

export const getThresholds = (
  memberId: string | undefined,
  type: string,
  paramString?: string
) =>
  API.kore.readings.getThreshold(
    memberId || "",
    associateReadingType(type),
    Session.crackCookieToken(coordinator),
    paramString
  );

export const setThresholds = (threshold: ThresholdInterface) => {
  return API.kore.readings.setThreshold(
    threshold,
    Session.crackCookieToken(coordinator)
  );
};

export const updateThreshold = (threshold: any) => {
  return API.kore.readings.updateThreshold(
    threshold?._id || "",
    threshold.value,
    Session.crackCookieToken(coordinator)
  );
};

export const removeThresholds = (threshold: ThresholdInterface | undefined) => {
  return API.kore.readings.removeThreshold(
    threshold?._id || "",
    Session.crackCookieToken(coordinator)
  );
};

export const GenerateTableHeaders = (
  type: any,
  spirometryVisibility?: Record<string, boolean>
) => {
  const { t } = useTranslation();
  let thresholdTableHeaders: CellProps[] = [];
  switch (associateReadingType(type)) {
    case bloodPressure.abbreviation:
      thresholdTableHeaders.push(
        { content: `${t(uSystolic)} ${t(uLow)}`, align: "center" },
        { content: `${t(uSystolic)} ${t(uHigh)}`, align: "center" },
        { content: `${t(uDiastolic)} ${t(uLow)}`, align: "center" },
        { content: `${t(uDiastolic)} ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case bloodGlucose.abbreviation:
      thresholdTableHeaders.push(
        { content: `Blood Sugar ${t(uLow)}`, align: "center" },
        { content: `Blood Sugar ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case spirometry.abbreviation:
      if (spirometryVisibility?.fvc !== false) {
        thresholdTableHeaders.push(
          { content: `FVC ${t(uLow)}`, align: "center" },
          { content: `FVC ${t(uHigh)}`, align: "center" },
        );
      }
      if (spirometryVisibility?.fev1 !== false) {
        thresholdTableHeaders.push(
          { content: `FEV1 ${t(uLow)}`, align: "center" },
          { content: `FEV1 ${t(uHigh)}`, align: "center" },
        );
      }
      if (spirometryVisibility?.fev1fvc !== false) {
        thresholdTableHeaders.push(
          { content: `FEV1/FVC ${t(uLow)}`, align: "center" },
          { content: `FEV1/FVC ${t(uHigh)}`, align: "center" },
        );
      }
      if (spirometryVisibility?.pef !== false) {
        thresholdTableHeaders.push(
          { content: `PEF ${t(uLow)}`, align: "center" },
          { content: `PEF ${t(uHigh)}`, align: "center" },
        );
      }
      thresholdTableHeaders.push({ content: t(uStatus), align: "center" });
      break;
    case oxygenSaturation.abbreviation:
      thresholdTableHeaders.push(
        { content: `Blood Oxygen ${t(uLow)}`, align: "center" },
        { content: `Blood Oxygen ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case temperature.abbreviation:
      thresholdTableHeaders.push(
        { content: `Temperature ${t(uLow)}`, align: "center" },
        { content: `Temperature ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case WEIGHT.abbreviation:
      thresholdTableHeaders.push(
        { content: `Weight ${t(uLow)}`, align: "center" },
        { content: `Weight ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
      case activity.abbreviation:
      thresholdTableHeaders.push(
        { content: `Activity ${t(uLow)}`, align: "center" },
        { content: `Activity ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case SLEEP.abbreviation:
      thresholdTableHeaders.push(
        { content: `Sleep ${t(uLow)}`, align: "center" },
        { content: `Sleep ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
    case heartRate.abbreviation:
      thresholdTableHeaders.push(
        { content: `Pulse Rate ${t(uLow)}`, align: "center" },
        { content: `Pulse Rate ${t(uHigh)}`, align: "center" },
        { content: t(uStatus), align: "center" }
      );
      break;
  }
  return thresholdTableHeaders;
};

export const generateList = (
  rawList: ThresholdInterface[],
  spirometryVisibility?: Record<string, boolean>
): BodyProps[] => {
  return rawList.map((item) => {
    const {
      systolic,
      diastolic,
      pulse,
      glucose,
      FVC,
      FEV1,
      FEV1_FVC,
      PEF,
      spo2,
      temp,
      weight,
      steps,
      sleep
    } = item.value;
    let breachIcon = <></>;
    let adjustedTableBody: CellProps[] = [];

    if (item.breachType === "high") {
      breachIcon = <RedAlertIcon />;
    } else if (item.breachType === "moderate") {
      breachIcon = <YellowAlertIcon />;
    }
    switch (item.readingType) {
      case bloodPressure.abbreviation:
        adjustedTableBody.push(
          {
            content: systolic?.low ? `${systolic?.low}` : "-",
            align: "center"
          },
          {
            content: systolic?.high ? `${systolic?.high}` : "-",
            align: "center"
          },
          {
            content: diastolic?.low ? `${diastolic?.low}` : "-",
            align: "center"
          },
          {
            content: diastolic?.high ? `${diastolic?.high}` : "-",
            align: "center"
          },
          { content: breachIcon, align: "center" }
        );
        break;
      case bloodGlucose.abbreviation:
        adjustedTableBody.push(
          { content: glucose?.low ? `${glucose?.low}` : "-", align: "center" },
          {
            content: glucose?.high ? `${glucose?.high}` : "-",
            align: "center"
          },
          { content: breachIcon, align: "center" }
        );
        break;
      case spirometry.abbreviation:
        if (spirometryVisibility?.fvc !== false) {
          adjustedTableBody.push(
            { content: FVC?.low ? `${FVC?.low}` : "-", align: "center" },
            { content: FVC?.high ? `${FVC?.high}` : "-", align: "center" }
          );
        }
        if (spirometryVisibility?.fev1 !== false) {
          adjustedTableBody.push(
            { content: FEV1?.low ? `${FEV1?.low}` : "-", align: "center" },
            { content: FEV1?.high ? `${FEV1?.high}` : "-", align: "center" }
          );
        }
        if (spirometryVisibility?.fev1fvc !== false) {
          adjustedTableBody.push(
            {
              content: FEV1_FVC?.low ? `${FEV1_FVC?.low}`: "-",
              align: "center"
            },
            {
              content: FEV1_FVC?.high ? `${FEV1_FVC?.high}`: "-",
              align: "center"
            }
          );
        }
        if (spirometryVisibility?.pef !== false) {
          adjustedTableBody.push(
            { content: PEF?.low ? `${PEF?.low}`: "-", align: "center" },
            { content: PEF?.high ? `${PEF?.high}`: "-", align: "center" }
          );
        }
        adjustedTableBody.push(
          { content: breachIcon, align: "center" }
        );
        break;
      case oxygenSaturation.abbreviation:
        adjustedTableBody.push(
          { content: spo2?.low ? `${spo2?.low}` : "-", align: "center" },
          { content: spo2?.high ? `${spo2?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case temperature.abbreviation:
        adjustedTableBody.push(
          { content: temp?.low ? `${temp?.low}` : "-", align: "center" },
          { content: temp?.high ? `${temp?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case WEIGHT.abbreviation:
        adjustedTableBody.push(
          { content: weight?.low ? `${weight?.low}` : "-", align: "center" },
          { content: weight?.high ? `${weight?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case activity.abbreviation:
        adjustedTableBody.push(
          { content: steps?.low ? `${steps?.low}` : "-", align: "center" },
          { content: steps?.high ? `${steps?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case SLEEP.abbreviation:
        adjustedTableBody.push(
          { content: sleep?.low ? `${sleep?.low}` : "-", align: "center" },
          { content: sleep?.high ? `${sleep?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
      case heartRate.abbreviation:
        adjustedTableBody.push(
          { content: pulse?.low ? `${pulse?.low}` : "-", align: "center" },
          { content: pulse?.high ? `${pulse?.high}` : "-", align: "center" },
          { content: breachIcon, align: "center" }
        );
        break;
    }
    return { cells: adjustedTableBody };
  });
};
